import React, { useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { PROVIDER_SECTION_BREADCRUMB } from './provider-breadcrumb'
import { useApiListProvider } from '../../../../../api-new/state/auth'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { IconDetails, IconDomain, IconReload } from '../../../../../shared/icon/icon'
import { useLoad } from '../../../../../shared/hooks/load'
import { domainTone, overrideDetail } from '../../../../../shared/component/status-pill'
import { normalizeDomain } from '../../../../../shared/form-new/form-tags'
import { FleetPage, FleetSection, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { DomainPill } from '../../../../../shared/sso-workspace/pill'
import { TXT_KEEP_HINT, TxtRecordFields } from '../../../../../shared/sso-workspace/txt-record'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { UnbindDialog } from '../../../../../shared/sso-workspace/unbind-dialog'
import { requireSSOHint } from '../../../../../shared/sso-workspace/require-sso'
import { isConnection } from '../../../../../shared/sso-workspace/connection-use'
import { authDelete, authGet, authPost, authPut } from '../../../../../shared/sso-workspace/auth-api'
import { formatAgo, formatDate, formatDateTime, plural, serverMessage } from '../../../../../shared/sso-workspace/format'
import { overrideUntilThrough } from './provider-domain-actions'
import { ProviderRollup } from './provider-rollup'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { dayOf } from '../../../../../shared/date/local-time'
import { OrgPicker } from '../../../../../shared/org-search/org-picker'
import { organizationNameOf } from '../../../../../shared/org-search/org-search-api'
import { useOrganizationName } from '../../../../../shared/org-search/use-organization-names'

// A domain's owner: an organization (picked from content's organization
// search, never a list of every organization) or EducationXR.
const PLATFORM_HELD = { value: '', label: 'EducationXR (platform-held)' }

const STATE_FILTERS = [
  { value: '', label: 'Every state' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'at_risk', label: 'At risk' },
  { value: 'deactivated', label: 'Deactivated' }
]

const VIEW_FILTERS = [
  { value: '', label: 'All domains' },
  { value: 'unproven', label: 'Routing without DNS proof' },
  { value: 'unbound', label: 'Not bound to a connection' }
]

export const txtName = domain => domain.txtName || `_edxr-verify.${domain.domain}`

const ownerName = domain => domain.organizationName || 'EducationXR (platform-held)'

// What the state means for this domain right now, in one line under the pill.
export const domainStateLine = (domain, now = Date.now()) => {
  const tone = domainTone(domain, now)
  if (tone === 'overridden') return overrideDetail(domain)
  if (domain.state === 'pending') return domain.claimExpiresAt ? `Verify by ${formatDate(domain.claimExpiresAt)}, or the claim lapses.` : 'Waiting for its DNS record.'
  if (domain.state === 'at_risk') return domain.deadlineAt ? `DNS record missing. Deactivates ${formatDate(domain.deadlineAt)} unless it comes back.` : 'DNS record missing.'
  if (domain.state === 'deactivated') return 'DNS record gone past its grace period. Nobody is sent to single sign-on for it.'
  if (domain.state === 'verified') return domain.lastOkAt ? `Proven ${formatAgo(domain.lastOkAt, now)}.` : 'Verified.'
  return ''
}

// After a Check: what we found, in a sentence (audit F1: "check now" never
// said what happened).
export const checkResult = (before, after) => {
  if (!after) return `${before.domain} was checked.`
  if (after.check === 'unreachable') return "Couldn't reach DNS just now. Try again in a moment."
  if (after.lastOkAt && (!before.lastOkAt || new Date(after.lastOkAt) > new Date(before.lastOkAt))) {
    return `Found the TXT record for ${after.domain}. It's verified${before.state !== 'verified' ? ' and any waiting connection bound to it is now live' : ''}.`
  }
  return `Still no matching TXT record at ${txtName(after)}. DNS changes can take a few hours to appear; the daily check will keep looking.`
}

const useQuery = () => {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

// The platform-wide Domains list (spec 7), rebuilt for onboarding (audit
// F1/B1): each domain shows its state in words, who owns it, the connections
// it sends people to (bind and unbind here), and -- opened -- the TXT record
// to hand the school, Check now with the result in a sentence, Require SSO,
// override, transfer, notify and remove, every action reporting its own
// success or the server's reason. ?state=, ?providerID=, ?organizationID=
// and ?view= (unproven, unbound) come from links elsewhere (the rollup, the
// connection workspace).
export const ProviderDomainList = () => {
  const roleSysProvider = useRoleSysProvider()
  const canEdit = Boolean(roleSysProvider.accessUpdate)
  const canDelete = Boolean(roleSysProvider.accessDelete)
  const history = useHistory()
  const location = useLocation()
  const query = useQuery()

  const state = query.get('state') || ''
  const view = query.get('view') || ''
  const providerID = query.get('providerID') || ''
  const organizationID = query.get('organizationID') || ''
  const domainQuery = query.get('domain') || ''

  const apiListProvider = useApiListProvider()

  // The rows, tagged with the filters they were loaded for: a filter change
  // reads as loading at once (and remounts the grid, below) rather than
  // showing the previous filter's rows until the new answer arrives.
  const filterKey = [state, providerID, organizationID, domainQuery].join('|')
  const [loaded, setLoaded] = useState({ key: null, rows: undefined })
  const domains = loaded.key === filterKey ? loaded.rows : undefined
  const [selectedID, setSelectedID] = useState(null)
  const tableRef = useRef()
  const [providers, setProviders] = useState([])
  const [status, setStatus] = useState('')
  const [error, cause, setError] = useErrorLine()
  const [adding, setAdding] = useState(false)

  const [confirm, setConfirm] = useState(null) // { title, body, confirmText, run }
  const [confirmError, confirmFailure, setConfirmError] = useErrorLine()
  const [unbinding, setUnbinding] = useState(null) // { domain, connection }
  const [unbindError, unbindFailure, setUnbindError] = useErrorLine()
  const [busy, setBusy] = useState(false)

  const setParam = (key, value) => {
    const next = new URLSearchParams(location.search)
    value ? next.set(key, value) : next.delete(key)
    history.replace({ search: next.toString() ? `?${next.toString()}` : '' })
  }

  const load = async () => {
    try {
      setLoaded({ key: filterKey, rows: (await authGet('provider-domain', { state, providerID, organizationID, domain: domainQuery })) || [] })
    } catch (err) {
      setLoaded({ key: filterKey, rows: null })
    }
  }

  const loadProviders = async () => {
    try { setProviders((await apiListProvider.get()) || []) } catch (err) { setProviders([]) }
  }

  useLoad(() => loadProviders(), [])

  const providerByID = useMemo(() => {
    const map = {}
    providers.forEach(p => { map[p.id] = p })
    return map
  }, [providers])
  const connectionName = p => (p ? p.displayName || p.providerName : 'Unknown connection')

  // ?organizationID= (a link from an organization): named by lookup.
  const filterOrgName = useOrganizationName(organizationID)
  const filterProvider = providerID && providerByID[providerID]

  const visible = useMemo(() => {
    if (!Array.isArray(domains)) return domains
    if (view === 'unproven') return domains.filter(d => d.overrideAt && !d.lastOkAt)
    if (view === 'unbound') return domains.filter(d => !(d.providerIDs || []).length)
    return domains
  }, [domains, view])

  // One action: clear the old message, run it, say what happened (or the
  // server's reason), and reload the list.
  const act = async (fn, success, failure) => {
    setError(null)
    setStatus('')
    try {
      const result = await fn()
      setStatus(typeof success === 'function' ? success(result) : success)
      await load()
      return true
    } catch (err) {
      setError(serverMessage(err, failure), err)
      return false
    }
  }

  const handleCheck = domain =>
    act(() => authPost(`provider-domain/${domain.id}/check`), after => checkResult(domain, after), `${domain.domain} couldn't be checked.`)

  const handleSSORequired = domain =>
    act(() => authPut(`provider-domain/${domain.id}/sso-required`, { required: !domain.ssoRequired }),
      `${domain.domain}: single sign-on is ${domain.ssoRequired ? 'no longer required' : 'now required'}.`,
      `Single sign-on couldn't be ${domain.ssoRequired ? 'made optional' : 'required'} for ${domain.domain}.`)

  const handleBind = async (domain, id) => {
    const ok = await act(() => authPost(`provider-domain/${domain.id}/bindings`, { providerID: Number(id) }),
      `Bound ${domain.domain} to ${connectionName(providerByID[id])}.`, `${domain.domain} couldn't be bound.`)
    // A routable domain takes a waiting connection live on bind.
    if (ok) await loadProviders()
  }

  const handleUnbind = async deactivate => {
    const { domain, connection } = unbinding
    setBusy(true)
    setUnbindError(null)
    try {
      await authDelete(`provider-domain/${domain.id}/bindings/${connection.id}${deactivate ? '?deactivate=true' : ''}`)
    } catch (err) {
      setUnbindError(serverMessage(err, `${domain.domain} couldn't be unbound.`), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setUnbinding(null)
    setStatus(`Unbound ${domain.domain} from ${connectionName(connection)}${deactivate ? ' and deactivated it' : ''}.`)
    await Promise.all([load(), loadProviders()])
  }

  const askConfirm = spec => { setConfirmError(null); setConfirm(spec) }
  const runConfirm = async () => {
    setBusy(true)
    setConfirmError(null)
    let result
    try {
      result = await confirm.run()
    } catch (err) {
      setConfirmError(serverMessage(err, confirm.failure), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setStatus(typeof confirm.success === 'function' ? confirm.success(result) : confirm.success)
    setConfirm(null)
    await load()
  }

  const askRemove = domain => askConfirm({
    title: `Remove ${domain.domain}?`,
    body: `People at ${domain.domain} stop being sent to single sign-on at once${(domain.providerIDs || []).length ? `, and it's unbound from ${plural(domain.providerIDs.length, 'connection')}` : ''}. Anyone can claim the domain again afterwards.`,
    confirmText: 'Remove domain',
    success: `Removed ${domain.domain}.`,
    failure: `${domain.domain} couldn't be removed.`,
    run: () => authDelete(`provider-domain/${domain.id}`)
  })

  const askNotify = domain => askConfirm({
    title: `Email the people at ${domain.domain}?`,
    body: `Everyone at ${domain.domain} who signs in only with single sign-on gets an email to set a password, so they can still sign in while it's deactivated. It sends at most once a day.`,
    confirmText: 'Send emails',
    success: result => `Emailed ${plural((result && result.sent) || 0, 'person', 'people')} at ${domain.domain} a link to set a password.`,
    failure: `The emails for ${domain.domain} couldn't be sent.`,
    run: () => authPost(`provider-domain/${domain.id}/notify-members`)
  })

  const askLift = domain => askConfirm({
    title: `Lift the override on ${domain.domain}?`,
    body: domain.lastOkAt
      ? `${domain.domain} goes back to following its DNS record.`
      : `${domain.domain} has never been proven by DNS, so it moves to at risk with a fresh grace period and stops routing if the record never appears.`,
    confirmText: 'Lift override',
    success: `Lifted the override on ${domain.domain}.`,
    failure: `The override on ${domain.domain} couldn't be lifted.`,
    run: () => authDelete(`provider-domain/${domain.id}/override`)
  })

  // A public method stays offered to everyone without a domain, so it is
  // never "the last domain of an active connection" (review M6).
  const lastOfActive = connection => Boolean(connection && connection.active && !connection.public && (connection.domains || []).length <= 1)

  // Flattened display values: the grid sorts, quick-filters and exports the
  // cell VALUE, so each column's words live on the row.
  const gridRows = useMemo(
    () =>
      Array.isArray(visible)
        ? visible.map(d => ({
          ...d,
          stateLabel: domainTone(d),
          stateLine: domainStateLine(d),
          owner: ownerName(d),
          connections: (d.providerIDs || []).map(id => connectionName(providerByID[id])).join('; ')
        }))
        : visible,
    [visible, providerByID] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const gridList = useGridList(gridRows)

  const selected = Array.isArray(domains) ? domains.find(d => d.id === selectedID) : null

  const connectionsCell = row => {
    const d = row.data
    if (!d) return ''
    return (d.providerIDs || []).length
      ? <span className="d-inline-flex flex-wrap gap-1">
        {d.providerIDs.map(id =>
          <span key={id} className="sw-tag">
            <Link to={`/admin/sso/connections/${id}`}>{connectionName(providerByID[id])}</Link>
            {canEdit &&
              <button type="button" className="sw-tag-x" aria-label={`Unbind ${d.domain} from ${connectionName(providerByID[id])}`}
                onClick={() => { setUnbindError(null); setUnbinding({ domain: d, connection: providerByID[id] || { id, providerName: `connection ${id}` } }) }}>×</button>}
          </span>)}
      </span>
      : <span className="sw-hint">No connection yet</span>
  }

  const detail = d =>
    <DomainDetail domain={d} canEdit={canEdit} canDelete={canDelete} providers={providers}
      connectionName={connectionName} onBind={handleBind} onSSORequired={handleSSORequired}
      onOverride={(reason, until) => act(() => authPut(`provider-domain/${d.id}/override`, { reason, until }), `${d.domain} now routes by override${until ? ` until ${formatDate(until)}` : ''}.`, `${d.domain} couldn't be overridden.`)}
      onLift={() => askLift(d)}
      onTransfer={orgID => act(() => authPut(`provider-domain/${d.id}/owner`, { organizationID: orgID ? Number(orgID) : null }),
        `${d.domain} now belongs to ${orgID ? organizationNameOf(orgID) || 'that organization' : PLATFORM_HELD.label}.`,
        `${d.domain} couldn't be transferred.`)}
      onNotify={() => askNotify(d)} onRemove={() => askRemove(d)} />

  const scope = [
    domainQuery && `matching "${domainQuery}"`,
    organizationID && `owned by ${filterOrgName || `organization ${organizationID}`}`,
    filterProvider && `bound to ${connectionName(filterProvider)}`
  ].filter(Boolean).join(', ')

  return (
    <BreadcrumbView parent={PROVIDER_SECTION_BREADCRUMB} Icon={IconDomain} text="Domains">
      <FleetPage
        tabs
        title="Domains"
        purpose="The email domains that send people to single sign-on. A domain routes once its DNS record proves who controls it, or while EducationXR overrides it."
        actions={canEdit && <button type="button" className="btn sw-btn sw-btn-primary" aria-expanded={adding} onClick={() => setAdding(a => !a)}>Add domain</button>}>
        <ProviderRollup />

        {adding && <AddDomain onCancel={() => setAdding(false)}
          onAdded={async text => { setAdding(false); setStatus(text); await load() }} />}

        {scope &&
          <Notice>
            <p>Showing domains {scope}. <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => history.replace({ search: '' })}>Show every domain</button></p>
          </Notice>}

        {error && <p className="text-danger small mt-3 mb-0" role="alert">{error}</p>}
        {error && <ErrorReference error={cause} />}

        <div className="sw-toolbar mt-3">
          <select className="form-select form-select-sm" aria-label="State" value={state} onChange={e => setParam('state', e.target.value)}>
            {STATE_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="form-select form-select-sm" aria-label="Show" value={view} onChange={e => setParam('view', e.target.value)}>
            {VIEW_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {status && <p className="sw-status" role="status" aria-live="polite">{status}</p>}
        </div>
        {domains === null && <p className="text-danger small" role="alert">The domains couldn't be loaded. Reload to try again.</p>}

        <FleetGrid title="domains" tableRef={tableRef} exportName="domains">
          {/* Keyed on the server-side filters: the grid fetches in onReady
              (house pattern), so a different filter is picked up by remounting. */}
          <TableAgClient
            key={filterKey}
            ref={tableRef}
            apiList={gridList}
            onReady={load}
            sortCol="domain"
            getRowClass={params => (params.data && params.data.id === selectedID ? 'sw-hl' : '')}
            emptyState={{
              Icon: IconDomain,
              title: state || view || scope ? 'No matching domains' : 'No domains yet',
              message: state || view || scope ? 'No domains match these filters.' : "No domains yet. Add an organization's email domain, publish its TXT record, then bind it to its connection.",
              action: { label: 'Add domain', onClick: () => setAdding(true), show: canEdit && !adding }
            }}
            noMatchState={{ message: 'No domains match your search. Search domains, owners or connections.' }}>
            <ColumnFormat field="domain" header="Domain" render={domainCell} sortable />
            <ColumnFormat field="stateLabel" header="State" render={stateCell} sortable />
            <ColumnFormat field="owner" header="Owner" render={ownerCell} sortable />
            <ColumnFormat field="connections" header="Sends people to" render={connectionsCell} sortable />
            <ColumnFormat field="lastCheckedAt" header="Last checked" render={lastCheckedCell} sortable />
            <ColumnMenu
              menu={d =>
                <Menu small>
                  <MenuItem Icon={IconReload} text="check now" pin show={canEdit} onClick={() => handleCheck(d)} />
                  <MenuItem Icon={IconDetails} text="manage" pin show onClick={() => setSelectedID(id => (id === d.id ? null : d.id))} />
                </Menu>}
            />
          </TableAgClient>
        </FleetGrid>

        {selected &&
          <FleetSection id="manage" title={`Manage ${selected.domain}`}
            actions={<button type="button" className="btn btn-sm sw-btn" onClick={() => setSelectedID(null)}>Close</button>}>
            <div data-testid={`detail-${selected.id}`}>{detail(selected)}</div>
          </FleetSection>}
      </FleetPage>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? confirm.title : ''}
        body={confirm ? confirm.body : ''}
        confirmText={confirm ? confirm.confirmText : ''}
        busy={busy}
        error={confirmError}
        failure={confirmFailure}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
      <UnbindDialog
        open={Boolean(unbinding)}
        domain={unbinding ? unbinding.domain.domain : ''}
        connectionName={unbinding ? connectionName(unbinding.connection) : ''}
        lastOfActive={Boolean(unbinding && lastOfActive(unbinding.connection))}
        busy={busy}
        error={unbindError}
        failure={unbindFailure}
        onUnbind={() => handleUnbind(false)}
        onUnbindAndDeactivate={() => handleUnbind(true)}
        onCancel={() => setUnbinding(null)}
      />
    </BreadcrumbView>
  )
}

// "Add domain": claimed for an organization (or held by EducationXR).
const AddDomain = ({ onAdded, onCancel }) => {
  const [value, setValue] = useState('')
  const [owner, setOwner] = useState('')
  const [hint, setHint] = useState(null)
  const [error, cause, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)

  const submit = async e => {
    e.preventDefault()
    setError(null)
    const domain = normalizeDomain(value)
    if (!domain) {
      setHint(value.trim() ? `"${value.trim()}" isn't a domain.` : 'Enter the email domain, for example example.org.')
      return
    }
    setHint(null)
    setBusy(true)
    try {
      await authPost('provider-domain', { domain, organizationID: owner ? Number(owner) : null })
      onAdded(`Added ${domain}. Give the organization its TXT record (open the domain's Manage), or override it, then bind it to a connection.`)
    } catch (err) {
      setError(serverMessage(err, `${domain} couldn't be added.`), err)
      setBusy(false)
    }
  }

  return (
    <form className="sw-inline-panel mt-3" noValidate onSubmit={submit} aria-label="Add a domain">
      <div className="sw-rule-fields">
        <div className="sw-field">
          <label className="sw-label" htmlFor="domains-add-domain">Domain</label>
          <input id="domains-add-domain" className="form-control" value={value} placeholder="example.org" autoComplete="off" spellCheck={false} autoFocus
            aria-describedby="domains-add-domain-hint" onChange={e => setValue(e.target.value)} />
          <div id="domains-add-domain-hint" className={hint ? 'sw-required' : 'sw-hint'}>{hint || 'The part after the @ in people\'s work email.'}</div>
        </div>
        <div className="sw-field">
          <label className="sw-label" htmlFor="domains-add-owner">Owner</label>
          <OrgPicker id="domains-add-owner" label="Owner" value={owner} allOption={PLATFORM_HELD} onChange={setOwner} />
        </div>
      </div>
      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={cause} className="mt-0" />}
      <div className="sw-inline-actions">
        <button type="button" className="btn sw-btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn sw-btn sw-btn-primary" disabled={busy}>Add domain</button>
      </div>
    </form>
  )
}

// Everything to do with one domain, opened from its row.
const DomainDetail = ({ domain, canEdit, canDelete, providers, connectionName, onBind, onSSORequired, onOverride, onLift, onTransfer, onNotify, onRemove }) => {
  const [bindID, setBindID] = useState('')
  const [reason, setReason] = useState('')
  const [until, setUntil] = useState('')
  const [owner, setOwner] = useState(domain.organizationID ? String(domain.organizationID) : '')
  const [overrideHint, setOverrideHint] = useState(null)
  const overridden = domainTone(domain) === 'overridden'
  const bound = domain.providerIDs || []
  // A domain binds to a connection, never a sign-in method (auth refuses it).
  const bindable = providers.filter(p => !bound.includes(p.id) && isConnection(p)).sort((a, b) => connectionName(a).localeCompare(connectionName(b)))
  const idp = id => `domain-${domain.id}-${id}`

  const submitOverride = async e => {
    e.preventDefault()
    if (!reason.trim()) { setOverrideHint('Say why, for the audit trail.'); return }
    setOverrideHint(null)
    const ok = await onOverride(reason.trim(), until ? overrideUntilThrough(new Date(`${until}T00:00:00`)) : null)
    if (ok) { setReason(''); setUntil('') }
  }

  return (
    <div className="d-grid gap-3 py-1">
      {!domain.lastOkAt &&
        <div className="d-grid gap-2">
          <p className="sw-hint m-0">Ask the organization to publish this TXT record at their DNS host. The daily check picks it up, or use Check now.</p>
          <TxtRecordFields idPrefix={idp('txt')} domain={domain.domain} token={domain.txtToken} fullName={txtName(domain)} relativeName={domain.txtRelativeName} dnsHost={domain.dnsHost} />
          <p className="sw-hint m-0">{TXT_KEEP_HINT}</p>
        </div>}

      <dl className="sw-dl">
        <dt>Last checked</dt><dd>{domain.lastCheckedAt ? formatDateTime(domain.lastCheckedAt) : 'Never'}</dd>
        <dt>Last proven</dt><dd>{domain.lastOkAt ? formatDateTime(domain.lastOkAt) : 'Never'}</dd>
        {domain.state === 'pending' && domain.claimExpiresAt && <><dt>Claim lapses</dt><dd>{formatDateTime(domain.claimExpiresAt)}</dd></>}
        {domain.deadlineAt && <><dt>Deactivates</dt><dd>{formatDateTime(domain.deadlineAt)}</dd></>}
        {domain.overrideReason && <><dt>Override reason</dt><dd>{domain.overrideReason}</dd></>}
      </dl>

      {canEdit &&
        <div className="d-flex flex-wrap align-items-end gap-2">
          <div className="sw-field">
            <label className="sw-label" htmlFor={idp('bind')}>Bind to a connection</label>
            <select id={idp('bind')} className="form-select form-select-sm" value={bindID} onChange={e => setBindID(e.target.value)}>
              <option value="">Choose a connection</option>
              {bindable.map(p => <option key={p.id} value={p.id}>{connectionName(p)}{p.organizationName ? ` (${p.organizationName})` : ''}</option>)}
            </select>
          </div>
          <button type="button" className="btn btn-sm sw-btn" disabled={!bindID} onClick={async () => { await onBind(domain, bindID); setBindID('') }}>Bind</button>
        </div>}

      {canEdit &&
        <div className="form-check form-switch sw-switch">
          <input className="form-check-input" type="checkbox" role="switch" id={idp('sso')} checked={Boolean(domain.ssoRequired)} onChange={() => onSSORequired(domain)} />
          <label className="form-check-label" htmlFor={idp('sso')}>
            Require single sign-on
            <span className="sw-cell-sub">{requireSSOHint(domain.domain)}</span>
          </label>
        </div>}

      {canEdit && (overridden
        ? <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="sw-hint">{overrideDetail(domain)}</span>
          <button type="button" className="btn btn-sm sw-btn" onClick={onLift}>Lift override</button>
        </div>
        : <form className="d-flex flex-wrap align-items-end gap-2" noValidate onSubmit={submitOverride} aria-label={`Override ${domain.domain}`}>
          <div className="sw-field flex-grow-1" style={{ maxWidth: 360 }}>
            <label className="sw-label" htmlFor={idp('reason')}>Override: let it route without DNS proof</label>
            <input id={idp('reason')} className="form-control form-control-sm" value={reason} placeholder="Why, for example: organization's IT is changing DNS host" onChange={e => setReason(e.target.value)} />
            {overrideHint && <div className="sw-required">{overrideHint}</div>}
          </div>
          <div className="sw-field">
            <label className="sw-label" htmlFor={idp('until')}>Until (optional)</label>
            <input id={idp('until')} type="date" className="form-control form-control-sm" value={until} min={dayOf(Date.now())} onChange={e => setUntil(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-sm sw-btn">Override</button>
        </form>)}

      {canEdit &&
        <div className="d-flex flex-wrap align-items-end gap-2">
          <div className="sw-field">
            <label className="sw-label" htmlFor={idp('owner')}>Owner</label>
            <OrgPicker id={idp('owner')} label="Owner" value={owner} allOption={PLATFORM_HELD} onChange={setOwner} small />
          </div>
          <button type="button" className="btn btn-sm sw-btn" disabled={owner === (domain.organizationID ? String(domain.organizationID) : '')} onClick={() => onTransfer(owner)}>Transfer</button>
        </div>}

      <div className="d-flex flex-wrap gap-2">
        {canEdit && domain.state === 'deactivated' && <button type="button" className="btn btn-sm sw-btn" onClick={onNotify}>Email people a password link</button>}
        {canDelete && <button type="button" className="btn btn-sm sw-btn-outline-danger" onClick={onRemove}>Remove domain</button>}
      </div>
    </div>
  )
}

const domainCell = row => (row.value ? <code className="fw-semibold">{row.value}</code> : '')

const stateCell = row =>
  row.data
    // One line (a grid row is one line high): the pill, then what it means,
    // in full on hover.
    ? <span title={row.data.stateLine || undefined}>
      <DomainPill tone={row.data.stateLabel} title={overrideDetail(row.data) || undefined} />
      {row.data.stateLine && <span className="sw-hint ms-2">{row.data.stateLine}</span>}
    </span>
    : ''

const ownerCell = row => (row.data ? row.data.organizationName || <span className="sw-hint">EducationXR (platform-held)</span> : '')

const lastCheckedCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : <span className="sw-hint">Never</span>)
